import { EOgqlJobState, IOgqlJobResult } from '@opengov/ogql-types';
import { EQueryType, IReportConfiguration } from '@opengov/reporting-types';

import { reportDataTableAgGridActionCreators } from '../../../../reportExplorer/ReportDataTableAgGrid/state';
import { reportExplorerActionCreators } from '../../../../reportExplorer/state';
import { DataTableStatus } from '../../../../reportExplorer/types/reportExplorerTypes';
import { IReportLocale } from '../../../locale';
import { MuiToastTypes } from '../../../helpers/utils';
import { reportActionCreators } from '../../../report/state';
import { runV0Query } from './runQueries';

interface IDetailTableQuery {
  reportConfiguration: IReportConfiguration;
  reportMsgs: IReportLocale;
  useQueryService?: boolean;
  isStandardTemplate: boolean;
  lensId?: string;
  queryId?: string;
  templateId?: string;
}

export const detailTableQuery = async ({
  reportConfiguration,
  reportMsgs,
  useQueryService,
  isStandardTemplate,
  lensId,
  queryId = '',
  templateId
}: IDetailTableQuery): Promise<IOgqlJobResult | undefined> => {
  return runV0Query({
    lensId,
    reportId: reportConfiguration.reportId,
    queryId,
    queryType: EQueryType.detailTableQuery,
    useLargeScaleQueryLimit: false,
    templateId,
    body: isStandardTemplate ? { reportConfiguration } : reportConfiguration,
    useQueryService,
    isStandardTemplate
  })
    .then((result) => {
      return handleDetailTableQueryResult(result, reportMsgs, reportConfiguration, EQueryType.detailTableQuery);
    })
    .catch((_e) => {
      reportActionCreators.toggleMuiToast({
        type: MuiToastTypes.FAILURE,
        headerText: reportMsgs.query.failure.headerText,
        bodyText: reportMsgs.query.failure.bodyText
      });
      return undefined;
    });
};

export const detailTableCountQuery = async ({
  reportConfiguration,
  reportMsgs,
  useQueryService,
  isStandardTemplate,
  lensId,
  queryId = '',
  templateId
}: IDetailTableQuery): Promise<IOgqlJobResult | undefined> => {
  return runV0Query({
    lensId,
    templateId,
    reportId: reportConfiguration.reportId,
    queryId,
    queryType: EQueryType.detailTableCountQuery,
    body: isStandardTemplate ? { reportConfiguration } : reportConfiguration,
    useQueryService,
    isStandardTemplate
  })
    .then((result) => {
      return handleDetailTableQueryResult(result, reportMsgs, reportConfiguration, EQueryType.detailTableCountQuery);
    })
    .catch((e) => {
      console.error(e);
      reportActionCreators.toggleMuiToast({
        type: MuiToastTypes.FAILURE,
        headerText: reportMsgs.query.failure.headerText,
        bodyText: reportMsgs.query.failure.bodyText
      });
      return undefined;
    });
};

export const detailTableSumQuery = async ({
  reportConfiguration,
  reportMsgs,
  useQueryService,
  isStandardTemplate,
  lensId,
  queryId = '',
  templateId
}: IDetailTableQuery): Promise<IOgqlJobResult | undefined> => {
  return runV0Query({
    lensId,
    queryId,
    templateId,
    reportId: reportConfiguration.reportId,
    queryType: EQueryType.detailTableSumQuery,
    body: isStandardTemplate ? { reportConfiguration } : reportConfiguration,
    useQueryService,
    isStandardTemplate
  })
    .then((result) => {
      return handleDetailTableQueryResult(result, reportMsgs, reportConfiguration, EQueryType.detailTableSumQuery);
    })
    .catch((e) => {
      console.error(e);
      reportActionCreators.toggleMuiToast({
        type: MuiToastTypes.FAILURE,
        headerText: reportMsgs.query.failure.headerText,
        bodyText: reportMsgs.query.failure.bodyText
      });
      return undefined;
    });
};

export const handleDetailTableQueryResult = (
  result: IOgqlJobResult,
  reportMsgs,
  reportConfiguration,
  queryType: EQueryType
) => {
  switch (result.state) {
    case EOgqlJobState.finished: {
      if (queryType === EQueryType.detailTableCountQuery) {
        const [[total]] = result.jobResult.data;

        reportExplorerActionCreators.updateDataTableTotalRowCount(total);
      } else if (queryType === EQueryType.detailTableSumQuery) {
        reportDataTableAgGridActionCreators.setSumJobResult(result.jobResult);
      } else {
        reportDataTableAgGridActionCreators.setJobResult(result.jobResult);
        reportExplorerActionCreators.updateDataTableStatus(DataTableStatus.Current);
        reportExplorerActionCreators.updatePaginationOffset(reportConfiguration.detailTableQuery.paginationOffset);
      }

      return result;
    }
    case EOgqlJobState.failed: {
      reportActionCreators.toggleMuiToast({
        type: MuiToastTypes.FAILURE,
        headerText: reportMsgs.query.failure.headerText,
        bodyText: reportMsgs.query.failure.bodyText
      });
      return result;
    }
    // we cancelled it .. it's not an error
    case EOgqlJobState.cancelled:
    default:
      return result;
  }
};
